import eventBus from "@/events/eventBus";
import GoingUtils from "@/utils/goingUtils";
import {GoingNewUtils} from "xmgoing-utils";
import {usePageModelsStore} from "@/store/modules/pageModels";
import {storeToRefs} from "pinia";
import {toRaw, unref, watch} from "vue";
import {
  appendClearedTagSyncFields,
  canColumnExpressOperator,
  isDateRangeColumn,
  isOrQueryRelation,
  matchColumnByQueryId,
  peekQuerySyncEcho,
  resetTagSearchWidget,
  resolveDateRangeInitQuery,
  type ParsedQueryItem,
} from "@/main-page/utils/gridQuerySyncUtils";
import {useGridTableStore} from "@/store/modules/gridTableStore";

/** 列头当前排序。只改列上的 sortOrder，不替换列对象，也不给没开排序的列补 sorter。 */
export type HeaderSortItem = { field: string; fieldId?: string; order: string };

const columnSorterBackup = new WeakMap<any, any>();

/** 保存列配置时用原来的 sorter。多列排序的 multiple 只留在当前列上，不写进个人列配置。 */
export function columnForPersist(column: any) {
    if (!column || typeof column !== 'object') {
        return column;
    }
    const rawColumn = toRaw(column);
    const backup = columnSorterBackup.has(column)
        ? columnSorterBackup.get(column)
        : (columnSorterBackup.has(rawColumn) ? columnSorterBackup.get(rawColumn) : undefined);
    if (backup === undefined) {
        return column;
    }
    return {
        ...rawColumn,
        sorter: backup,
    };
}

function toAntdSortOrder(order: string) {
    if (order === 'asc' || order === 'ascend') {
        return 'ascend';
    }
    return 'descend';
}

function columnMatchesSortField(column: any, field: string) {
    if (!column || !field) {
        return false;
    }
    return [column.dataIndex, column.propId, column.key, column.sortField]
        .filter((value) => value !== undefined && value !== null && value !== '')
        .some((value) => value === field);
}

function columnInHeaderSort(column: any, sort: HeaderSortItem) {
    return columnMatchesSortField(column, sort.field) || columnMatchesSortField(column, sort.fieldId || '');
}

/**
 * 直接改原列的 sortOrder。多列排序时临时把 sorter 换成 multiple，结束时还原，避免写进列配置。
 * 没开「字段排序」的列不动。
 */
export function applyColumnSortMarks(columns: any[], headerSorts: HeaderSortItem[] | null | undefined) {
    if (!Array.isArray(columns)) {
        return;
    }
    const sorts = Array.isArray(headerSorts) ? headerSorts : [];
    const multi = sorts.length > 1;
    columns.forEach((column) => {
        if (!column) {
            return;
        }
        if (columnSorterBackup.has(column)) {
            column.sorter = columnSorterBackup.get(column);
            columnSorterBackup.delete(column);
        }
        if (!column.sorter) {
            return;
        }
        const matchedIndex = sorts.findIndex((sort) => columnInHeaderSort(column, sort));
        const sortOrder = matchedIndex >= 0 ? sorts[matchedIndex].order : null;
        column.sortOrder = sortOrder;
        const rawColumn = toRaw(column);
        Object.defineProperty(rawColumn, 'sortOrder', {
            value: sortOrder,
            writable: true,
            configurable: true,
            enumerable: false,
        });
        if (multi) {
            columnSorterBackup.set(column, column.sorter);
            column.sorter = {
                multiple: matchedIndex >= 0 ? (sorts.length - matchedIndex) : 0,
            };
        }
    });
}

function sortFieldKeys(item: HeaderSortItem) {
    return [item.field, item.fieldId].filter((value) => value !== undefined && value !== null && value !== '');
}

function isSameSortField(left: HeaderSortItem, right: HeaderSortItem) {
    const rightKeys = sortFieldKeys(right);
    return sortFieldKeys(left).some((key) => rightKeys.includes(key));
}

function reportedHeaderSorts(sorter: any): HeaderSortItem[] {
    const toItem = (raw: any): HeaderSortItem => ({
        field: raw.field || raw.columnKey,
        fieldId: raw.column?.sortField || raw.field || raw.columnKey,
        order: toAntdSortOrder(raw.order),
    });
    if (Array.isArray(sorter)) {
        return sorter
            .filter((item) => item?.order && (item.field || item.columnKey))
            .map(toItem);
    }
    if (sorter?.order && (sorter.field || sorter.columnKey)) {
        return [toItem(sorter)];
    }
    return [];
}

/**
 * 只有一列排序时，列头点击替换成这一列。
 * 已经是多列排序时，只改被点的那一列：升序变降序，降序则从排序里去掉，没参与排序的列加进去。其余列和顺序不动。
 */
export function resolveClickedHeaderSort(sorter: any, prev: HeaderSortItem[] = []): HeaderSortItem[] {
    const reported = reportedHeaderSorts(sorter);
    if (prev.length <= 1) {
        return reported;
    }
    const next: HeaderSortItem[] = [];
    prev.forEach((old) => {
        const hit = reported.find((item) => isSameSortField(old, item));
        if (hit) {
            next.push({
                field: old.field,
                fieldId: old.fieldId || old.field,
                order: hit.order,
            });
        }
    });
    reported.forEach((item) => {
        if (!prev.some((old) => isSameSortField(old, item))) {
            next.push(item);
        }
    });
    return next;
}

function headerSortToOrderList(sorts: HeaderSortItem[]) {
    if (!sorts.length) {
        return {list: []};
    }
    return {
        list: sorts.map((item) => ({
            fieldId: item.fieldId || item.field,
            order: item.order === 'ascend' ? 'asc' : 'desc',
        })),
    };
}

export function parseHeaderSorts(orderList: any): HeaderSortItem[] {
    let parsed = orderList;
    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            return [];
        }
    }
    const list = Array.isArray(parsed?.list) ? parsed.list : (Array.isArray(parsed) ? parsed : []);
    return list
        .map((item) => ({
            field: item?.fieldId || item?.sortField || item?.propId || item?.dataIndex,
            order: toAntdSortOrder(item?.order),
        }))
        .filter((item) => !!item.field);
}

export function useColFilterHooks({emits, props, clientPagin, columns}) {

    const curDataIndex: any = ref([]);
    const preQueryParams: any = ref({});
    const clearSearchFlag = ref(GoingNewUtils.getUUChar(32));
    const headerSort = ref<HeaderSortItem[]>([]);
    let headerSortMarked = false;

    const applyHeaderSortMarks = () => {
        if (!headerSort.value.length && !headerSortMarked) {
            return;
        }
        headerSortMarked = true;
        applyColumnSortMarks(unref(columns), headerSort.value);
    };

    watch(headerSort, applyHeaderSortMarks);
    watch(columns, () => {
        if (headerSortMarked) {
            applyColumnSortMarks(unref(columns), headerSort.value);
        }
    });
    const gridTableStore: any = useGridTableStore();

    const pageModelsStore = usePageModelsStore();
    const {state}: any = storeToRefs(pageModelsStore);

    const resetColQueryStateAfterOr = function () {
        preQueryParams.value = {};
        curDataIndex.value = [];
        gridTableStore.setCommittedColQueryParams(unref(props).key, {});
    }

    const shouldInheritColQueryParams = function () {
        const tableObj = gridTableStore.getGridObjByKey(unref(props).key);
        return !isOrQueryRelation(tableObj?.paramsRel);
    }

    const persistCommittedColParams = function (queryParams: Record<string, any>) {
        preQueryParams.value = {...queryParams};
        gridTableStore.setCommittedColQueryParams(unref(props).key, {...queryParams});
    }

    let pendingClearedTagItems: ParsedQueryItem[] = [];

    const resetTagWidgetBeforeColQuery = function () {
        pendingClearedTagItems = resetTagSearchWidget(unref(props).key, gridTableStore);
    }

    const applyPendingTagFieldClears = function (params: Record<string, any>) {
        if (pendingClearedTagItems.length) {
            appendClearedTagSyncFields(params, pendingClearedTagItems, []);
            pendingClearedTagItems = [];
        }
    }

    const runColQuery = function (params: Record<string, any>) {
        const inheritPrevious = shouldInheritColQueryParams();
        const queryParams = inheritPrevious
            ? {...unref(preQueryParams)}
            : {};
        applyPendingTagFieldClears(queryParams);
        Object.assign(queryParams, params);
        emits('flushData', queryParams);
        persistCommittedColParams(queryParams);
    }

    /**
     * 前端过滤
     */
    const handleSearchByClient = function ({selectedKeys, dataIndex, value}: any) {
        if (unref(state).searckKey[unref(props).key] == null) {
            unref(state).searckKey[unref(props).key] = {};
        }
        unref(state).searckKey[unref(props).key] = value || (selectedKeys && selectedKeys[0]);
        unref(state).searckField = dataIndex;
        unref(state).searchCol = true;
        nextTick(() => {
            unref(state).searchCol = false;
        })
    }
    /**
     * 列查询操作
     * @param selectedKeys
     * @param confirm
     * @param dataIndex
     */
    const handleSearch = function ({
                                       queryType,
                                       selectedKeys,
                                       likeFlag = true,
                                       confirm,
                                       dataIndex,
                                       value,
                                       dataIndexTwo,
                                       valueTwo,
                                       originalDataIndex
                                   }) {
        resetTagWidgetBeforeColQuery();
        if (!shouldInheritColQueryParams()) {
            resetColQueryStateAfterOr();
        }
        if (unref(clientPagin) || unref(props).filterFlag) {
            handleSearchByClient({
                queryType,
                selectedKeys,
                confirm,
                dataIndex,
                value,
                dataIndexTwo,
                valueTwo,
                originalDataIndex
            });
            return;
        }
        const params = {};
        const column = unref(columns).find((item) => {
            if ((dataIndex + '').indexOf("_") > 0) {
                return item.dataIndex === (dataIndex + '').split("_")[0]
            }
            return item.dataIndex === dataIndex
        });
        if (dataIndexTwo) {
            const iconKey = originalDataIndex || column?.dataIndex || column?.filterField;
            if (iconKey && !unref(curDataIndex).includes(iconKey)) {
                unref(curDataIndex).push(iconKey);
            }
            if (column && column.controlType === "DatePicker__withTime") {
                params[dataIndex] = value + " 00:00:00";
                params[dataIndexTwo] = valueTwo + " 23:59:59";
            } else {
                params[dataIndex] = value;
                params[dataIndexTwo] = valueTwo;
            }

            // clearSearchFlag.value=GoingNewUtils.getUUChar(32);
            runColQuery(params);
            return
        }
        const fieldId = unref(column)?.filterField || unref(column)?.dataIndex;
        if (queryType === 'in') {
            let hasValue = false; // 判断是否有值
            if (unref(selectedKeys[0]) && Array.isArray(unref(selectedKeys[0]))) {
                const value = unref(selectedKeys[0]);
                const firstValue = value[0];
                if (firstValue && firstValue.length > 0) {
                    hasValue = true;
                }
            }
            if (hasValue) {
                // 有值 需要查询
                // 只有当 dataIndex 不存在于 curDataIndex 中时，才添加它
                if (!unref(curDataIndex).includes(dataIndex)) {
                    unref(curDataIndex).push(dataIndex);
                }
                params[fieldId + "_in"] = unref(selectedKeys[0]).join(";");
            } else {
                // 没有值 需要清除
                // 只有当 dataIndex 存在于 curDataIndex 中时，才移除它
                const index = unref(curDataIndex).indexOf(dataIndex);
                if (index > -1) {
                    unref(curDataIndex).splice(index, 1);
                }
                params[fieldId + "_in"] = undefined;
            }
            runColQuery(params);
            // clearSearchFlag.value=GoingNewUtils.getUUChar(32);
            return;
        }
        if (selectedKeys && selectedKeys[0]) {
            // 清空之前的查询条件 再重新赋值 清空时设置 undefined 而不是 delete，确保 flushDataByColQuery 能识别到该字段被清空
            unref(params)[fieldId] = undefined;
            unref(preQueryParams)[fieldId] = undefined;
            if (unref(likeFlag)) {
                unref(params)[fieldId + "_eq"] = undefined;
                unref(preQueryParams)[fieldId + "_eq"] = undefined;
                params[fieldId + "_like"] = selectedKeys[0];
            } else {
                unref(params)[fieldId + "_like"] = undefined;
                unref(preQueryParams)[fieldId + "_like"] = undefined;
                params[fieldId + "_eq"] = selectedKeys[0];
            }
            // 只有当 dataIndex 不存在于 curDataIndex 中时，才添加它
            if (!unref(curDataIndex).includes(dataIndex)) {
                unref(curDataIndex).push(dataIndex);
            }

            runColQuery(params);
        } else {
            const queryParams = shouldInheritColQueryParams()
                ? {...unref(preQueryParams)}
                : {};
            // 清空时设置 undefined 而不是 delete，确保 flushDataByColQuery 能识别到该字段被清空
            queryParams[fieldId] = undefined;
            queryParams[fieldId + "_ge"] = undefined;
            queryParams[fieldId + "_in"] = undefined;
            queryParams[fieldId + "_le"] = undefined;
            queryParams[fieldId + "_eq"] = undefined;
            queryParams[fieldId + "_like"] = undefined;
            emits('flushData', queryParams);
            persistCommittedColParams(queryParams);
            // 从 curDataIndex 中移除该字段，避免图标仍然显示为激活状态
            const index = unref(curDataIndex).indexOf(dataIndex);
            if (index > -1) {
                unref(curDataIndex).splice(index, 1);
            }
        }
        // clearSearchFlag.value=GoingNewUtils.getUUChar(32);
    }

    /**
     * 下拉选项查询
     * @param pag
     * @param filters
     * @param sorter
     */
    const handleTableChange = function (
        pag: { pageSize: number; current: number },
        filters: any,
        sorter: any,
        extra?: { action?: string },
    ) {
        resetTagWidgetBeforeColQuery();
        const params: any = {};
        const paramsRel = 'and';
        // 只有点列头排序才改排序。筛选、翻页沿用已有 orderList。
        const sortAction = extra?.action === 'sort' || (extra?.action == null && sorter && (Array.isArray(sorter) || sorter.field));
        if (sortAction) {
            const nextSort = resolveClickedHeaderSort(sorter, headerSort.value);
            headerSort.value = nextSort;
            // 空 list 也要带上，避免沿用排序弹窗留下的多列排序
            params.orderList = headerSortToOrderList(nextSort);
        }
        Object.keys(filters).forEach((fItem) => {
            if (filters[fItem] && Array.isArray(filters[fItem])) {
                const column = unref(columns).find((item) => {
                    return item.dataIndex === fItem
                });
                const fieldId = column.filterField || column.dataIndex;
                if (Array.isArray(filters[fItem])) {
                    // 只有当 fItem 不存在于 curDataIndex 中时，才添加它
                    if (!unref(curDataIndex).includes(fItem)) {
                        unref(curDataIndex).push(fItem);
                    }
                    params[fieldId + "_in"] = filters[fItem].join(";");
                } else {
                    params[fieldId] = filters[fItem];
                }
            }
        })
        if (unref(props).clientPagin) {
            emits('clientFlushData', params, paramsRel);
        } else {
            const colParams = shouldInheritColQueryParams()
                ? {...unref(preQueryParams)}
                : {};
            applyPendingTagFieldClears(colParams);
            Object.assign(colParams, params);
            persistCommittedColParams(colParams);
            emits('flushData', colParams, paramsRel);
        }
    }

    const queryParamsList: any = ref([]);

    const _resetQuery = function () {
        curDataIndex.value = [];
        unref(queryParamsList).splice(0, unref(queryParamsList).length);
        clearSearchFlag.value = GoingNewUtils.getUUChar(32);
        clearAllFilters();
    }

    const clearAllFilters = function () {
        preQueryParams.value = {};
        gridTableStore.setCommittedColQueryParams(unref(props).key, {});
        unref(columns).forEach(column => {
            const keys = [column.dataIndex, column.filterField].filter(Boolean);
            const eventPrefix = GoingUtils.getEventCtlKey(unref(props).key);
            keys.forEach((fieldKey) => {
                eventBus.emit(eventPrefix + fieldKey + '_resetQuery');
            });
        });
    }

    const handleReset = ({selectedKeys, dataIndex}) => {
        if (unref(clientPagin) || unref(props).filterFlag) {
            handleSearchByClient({selectedKeys, dataIndex});
            return;
        }
    }

    const isActiveColumn = function (column) {
        return unref(curDataIndex).includes(column.dataIndex) || unref(curDataIndex).includes(column.filterField)
    }

    const emitColChangeItemValue = function (fieldKey, queryParam, col) {
        const eventPrefix = GoingUtils.getEventCtlKey(unref(props).key);
        const keys = [fieldKey, col?.dataIndex, col?.filterField, col?.propId].filter(Boolean);
        [...new Set(keys)].forEach((key) => {
            eventBus.emit(eventPrefix + key + '_changeItemValue', queryParam);
        });
    }

    const hasQueryItemValue = function (value) {
        return value !== undefined && value !== null && value !== '';
    }

    /**
     * 清空列查询id
     */
    const clearColQueryId = (queryParams) => {
        queryParamsList.value = [];
        unref(curDataIndex).splice(0, unref(curDataIndex).length);
        const fieldGroupMap = new Map();
        Object.keys(queryParams).forEach((queryId) => {
            if (queryId === 'moreQueryConfigs') {
                return;
            }
            // 连表查询的情况
            let relQueryId = queryId;
            const res = queryId.split('.');
            if (res.length > 1) {
                relQueryId = res[1];
            }
            const queryIdAry = relQueryId.split("_");
            const fieldKey = queryIdAry[0];
            const operator = queryIdAry[1] ? queryIdAry[1] : 'like';
            if (!fieldGroupMap.has(fieldKey)) {
                fieldGroupMap.set(fieldKey, []);
            }
            fieldGroupMap.get(fieldKey).push({
                queryId,
                operator,
                queryValue: queryParams[queryId],
            });
        })
        const syncedFieldKeys = new Set();
        fieldGroupMap.forEach((items, fieldKey) => {
            const col = matchColumnByQueryId(unref(columns), fieldKey);
            const hasRangeOps = items.some((item) => item.operator === 'ge' || item.operator === 'le');
            let queryValue;
            let operator = items[items.length - 1]?.operator || 'like';
            if (hasRangeOps || isDateRangeColumn(col)) {
                const rangeQuery = resolveDateRangeInitQuery(
                    items.map((item) => ({
                        queryId: fieldKey,
                        operatorStr: item.operator,
                        value: item.queryValue,
                    }))
                );
                if (rangeQuery) {
                    queryValue = rangeQuery.queryValue;
                    operator = rangeQuery.operatorStr;
                }
            }
            if (!hasQueryItemValue(queryValue)) {
                const valuedItem = [...items].reverse().find((item) => hasQueryItemValue(item.queryValue));
                if (valuedItem) {
                    queryValue = valuedItem.queryValue;
                    operator = valuedItem.operator;
                }
            }
            if (hasQueryItemValue(queryValue) && !canColumnExpressOperator(col, operator)) {
                const echo = peekQuerySyncEcho(gridTableStore.getGridObjByKey(unref(props).key));
                // 查询表单回写列头：不可表达则清空。高级筛选→列头仍走原联动。
                if (!echo || echo.source === 'form') {
                    queryValue = '';
                }
            }
            if (!hasQueryItemValue(queryValue)) {
                const index = unref(curDataIndex).indexOf(fieldKey);
                if (index > -1) {
                    unref(curDataIndex).splice(index, 1);
                }
                items.forEach((item) => {
                    delete unref(preQueryParams)[item.queryId];
                });
                delete unref(preQueryParams)[fieldKey];
                delete unref(preQueryParams)[fieldKey + '_like'];
                delete unref(preQueryParams)[fieldKey + '_eq'];
                delete unref(preQueryParams)[fieldKey + '_in'];
                delete unref(preQueryParams)[fieldKey + '_ge'];
                delete unref(preQueryParams)[fieldKey + '_le'];
                delete unref(preQueryParams)[fieldKey + '_between'];
                emitColChangeItemValue(fieldKey, {queryId: fieldKey, queryValue: '', operator}, col);
                return;
            }
            if (!unref(curDataIndex).includes(fieldKey)) {
                unref(curDataIndex).push(fieldKey);
            }
            const queryParam = {
                queryId: fieldKey,
                queryValue,
                operator
            };
            unref(queryParamsList).push(queryParam);
            if (operator === 'between' && typeof queryValue === 'string') {
                const parts = String(queryValue).split(';');
                unref(preQueryParams)[fieldKey + '_ge'] = parts[0] || '';
                unref(preQueryParams)[fieldKey + '_le'] = parts[1] || parts[0] || '';
                delete unref(preQueryParams)[fieldKey + '_between'];
            }
            emitColChangeItemValue(fieldKey, queryParam, col);
            syncedFieldKeys.add(fieldKey);
        })
        const {moreQueryConfigs} = queryParams;
        if (moreQueryConfigs) {
            moreQueryConfigs.searchField.forEach((fieldJson) => {
                if (syncedFieldKeys.has(fieldJson.key)) {
                    return;
                }
                const queryParam = {queryId: fieldJson.key, queryValue: fieldJson.value, operator: fieldJson.operator}
                unref(queryParamsList).push(queryParam);
                if (!unref(curDataIndex).includes(fieldJson.key)) {
                    unref(curDataIndex).push(fieldJson.key);
                }
                if (fieldJson.operator) {
                    unref(preQueryParams)[fieldJson.key + "_" + fieldJson.operator] = fieldJson.value;
                }
                const col = matchColumnByQueryId(unref(columns), fieldJson.key);
                emitColChangeItemValue(fieldJson.key, queryParam, col);
            })
        }
        Object.keys(unref(preQueryParams)).forEach((queryId) => {
            if (queryId === 'moreQueryConfigs') {
                return;
            }
            let relQueryId = queryId;
            const res = queryId.split('.');
            if (res.length > 1) {
                relQueryId = res[1];
            }
            const queryIdAry = relQueryId.split('_');
            const fieldKey = queryIdAry[0];
            const operator = queryIdAry[1];
            if ((operator === 'ge' || operator === 'le') && !syncedFieldKeys.has(fieldKey) && !fieldGroupMap.has(fieldKey)) {
                const col = matchColumnByQueryId(unref(columns), fieldKey);
                delete unref(preQueryParams)[queryId];
                delete unref(preQueryParams)[fieldKey + '_ge'];
                delete unref(preQueryParams)[fieldKey + '_le'];
                emitColChangeItemValue(fieldKey, {queryId: fieldKey, queryValue: '', operator: 'between'}, col);
            }
        });
    }

    const _syncColQuery = function (queryParams: Record<string, any>) {
        if (!queryParams || typeof queryParams !== 'object') {
            return;
        }
        clearColQueryId(queryParams);
    }

    // 查询、重置和带 orderList 的列表刷新都走这里，只改箭头，不发请求
    const _setHeaderSort = function (orderList: any) {
        headerSort.value = parseHeaderSorts(orderList);
    }

    onBeforeUnmount(() => {
        eventBus.off(GoingUtils.getEventCtlKey(unref(props).key) + '_resetQuery', _resetQuery);
        eventBus.off(GoingUtils.getEventCtlKey(unref(props).key) + '_syncColQuery', _syncColQuery);
        eventBus.off(GoingUtils.getEventCtlKey(unref(props).key) + '_setHeaderSort', _setHeaderSort);
    })

    onMounted(() => {
        eventBus.off(GoingUtils.getEventCtlKey(unref(props).key) + '_resetQuery', _resetQuery);
        eventBus.on(GoingUtils.getEventCtlKey(unref(props).key) + '_resetQuery', _resetQuery);
        eventBus.off(GoingUtils.getEventCtlKey(unref(props).key) + '_syncColQuery', _syncColQuery);
        eventBus.on(GoingUtils.getEventCtlKey(unref(props).key) + '_syncColQuery', _syncColQuery);
        eventBus.off(GoingUtils.getEventCtlKey(unref(props).key) + '_setHeaderSort', _setHeaderSort);
        eventBus.on(GoingUtils.getEventCtlKey(unref(props).key) + '_setHeaderSort', _setHeaderSort);
    })

    const getDefaultStr = function (colItem) {
        const queryObj = unref(queryParamsList).find((itemObj) => {
            return itemObj.queryId === colItem.dataIndex
                || itemObj.queryId === colItem.filterField
                || itemObj.queryId === colItem.propId;
        });
        if (queryObj) {
            return queryObj.queryValue;
        }
    }

    return {
        getDefaultStr,
        clearColQueryId,
        isActiveColumn,
        clearSearchFlag,
        handleReset,
        curDataIndex,
        handleSearch,
        handleTableChange
    }
}
